import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  TextField, Button, CircularProgress, Alert, Box, FormControl, InputLabel, Select, MenuItem,
  Snackbar, Grid, Paper, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS } from '../../../../config/api';

const EditUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchUserAndRoles = async () => {
      try {
        const [userResponse, rolesResponse] = await Promise.all([
          axios.get(`${API_ENDPOINTS.USERS.BASE}/${userId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          axios.get(API_ENDPOINTS.ROLES.BASE, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);

        const userData = userResponse.data.data;
        setUser(userData);
        setRoles(rolesResponse.data.data || []);
        setSelectedRole(userData.roles && userData.roles.length > 0 ? userData.roles[0].name : '');
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to fetch user details or roles',
          severity: 'error'
        });
        console.error('Fetch error:', error.response?.data || error.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserAndRoles();
    } else {
      setSnackbar({
        open: true,
        message: 'No user ID provided',
        severity: 'error'
      });
      setLoading(false);
    }
  }, [userId]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleUpdateUser = async () => {
    if (!user.name || !user.email || !selectedRole) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }
    try {
      setLoading(true);
      await axios.put(`${API_ENDPOINTS.USERS.BASE}/${userId}`, { ...user, role: selectedRole }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSnackbar({
        open: true,
        message: 'User updated successfully!',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update user',
        severity: 'error'
      });
      console.error('Update error:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser(prevUser => ({ ...prevUser, [name]: value }));
  };

  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!user) {
    return <Alert severity="warning">No user data available</Alert>;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/users')} 
              sx={{ mr: 2 }}
            >
              Back to Users
            </Button>
            <Typography variant="h5">
              Edit User
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="div">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={user.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={user.email || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={user.mobile || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={user.address || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedRole}
                    onChange={handleRoleChange}
                    required
                    labelId="user-role"
                    id="user-role"
                    label="Role"
                  >
                    <MenuItem value="">Select Role</MenuItem>
                    {roles.map(role => (
                      <MenuItem key={role.id} value={role.name}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateUser}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Update User'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/users')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default EditUser;